if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.mobileMql = window.matchMedia('(max-width: 749px)');

        this.onVariantGalleryNativeChange = this.handleVariantGalleryNativeChange.bind(this);
        this.onVariantGalleryResize = this.handleVariantGalleryResize.bind(this);

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        if (this.elements.thumbnails) {
          this.bindThumbnailButtons();

          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
            this.removeListSemantic();
          }
        }

        this.initVariantFilenameGalleryFilter();
      }

      disconnectedCallback() {
        if (this.variantGalleryUnsubscriber) {
          this.variantGalleryUnsubscriber();
        }

        const productInfo = this.closest('product-info');

        if (productInfo) {
          productInfo.removeEventListener('change', this.onVariantGalleryNativeChange);
        }

        if (this.mobileMql?.removeEventListener) {
          this.mobileMql.removeEventListener('change', this.onVariantGalleryResize);
        }
      }

      bindThumbnailButtons() {
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');

          if (!button || button.dataset.variantGalleryBound === 'true') return;

          button.dataset.variantGalleryBound = 'true';
          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
      }

      initVariantFilenameGalleryFilter() {
        const productInfo = this.closest('product-info');

        if (productInfo) {
          productInfo.addEventListener('change', this.onVariantGalleryNativeChange);
        }

        if (this.mobileMql?.addEventListener) {
          this.mobileMql.addEventListener('change', this.onVariantGalleryResize);
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          this.variantGalleryUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const sectionId = event?.data?.sectionId;

            if (sectionId && this.id !== `MediaGallery-${sectionId}`) return;

            window.setTimeout(() => {
              this.refreshGalleryElements();
              this.filterVariantGalleryByFilenameColor();
            }, 0);
          });
        }

        window.setTimeout(() => {
          this.refreshGalleryElements();
          this.filterVariantGalleryByFilenameColor();
        }, 0);
      }

      refreshGalleryElements() {
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.bindThumbnailButtons();
      }

      handleVariantGalleryNativeChange() {
        window.setTimeout(() => {
          this.refreshGalleryElements();
          this.filterVariantGalleryByFilenameColor();
        }, 350);
      }

      handleVariantGalleryResize() {
        window.setTimeout(() => {
          this.refreshGalleryElements();
          this.filterVariantGalleryByFilenameColor();
        }, 100);
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;

        const currentElement = event?.detail?.currentElement;

        if (!currentElement || currentElement.hidden || currentElement.classList.contains('variant-gallery-hidden')) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (!this.elements.viewer) return;

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]:not(.variant-gallery-hidden):not([hidden])`) ||
          this.elements.viewer.querySelector('[data-media-id]:not(.variant-gallery-hidden):not([hidden])') ||
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) {
          return;
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });

        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);

            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          this.resetVariantGallerySliders();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!activeMedia.hidden && !activeMedia.classList.contains('variant-gallery-hidden')) {
            if (!this.mql.matches || this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }

            const activeMediaRect = activeMedia.getBoundingClientRect();

            if (activeMediaRect.top > -0.5) return;

            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          }
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);

        if (!activeThumbnail) return;

        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        if (thumbnail.hidden || thumbnail.classList.contains('variant-gallery-hidden')) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));

        const button = thumbnail.querySelector('button');

        if (button) {
          button.setAttribute('aria-current', true);
        }

        if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');

        if (!image || !this.elements.liveRegion) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia = activeItem.querySelector('.deferred-media');

        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      filterVariantGalleryByFilenameColor() {
        if (!this.elements.viewer) return;

        const selectedColor = this.getSelectedVariantColor();

        if (!selectedColor) {
          this.showAllVariantGalleryMedia();
          return;
        }

        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));

        if (!mediaItems.length) return;

        const matchingMediaItems = mediaItems.filter((item) => this.mediaElementMatchesColor(item, selectedColor));

        if (!matchingMediaItems.length) {
          this.showAllVariantGalleryMedia();
          return;
        }

        const visibleMediaIds = new Set(matchingMediaItems.map((item) => item.dataset.mediaId));

        mediaItems.forEach((item) => {
          const shouldHide = !visibleMediaIds.has(item.dataset.mediaId);
          this.setVariantGalleryHidden(item, shouldHide);
          item.classList.remove('is-active');
        });

        this.filterVariantGalleryThumbnails(visibleMediaIds);
        this.filterVariantGalleryModal(selectedColor);
        this.updateVariantGalleryCounter(matchingMediaItems.length);

        const firstVisibleMedia = matchingMediaItems[0];

        if (firstVisibleMedia) {
          this.setVariantGalleryHidden(firstVisibleMedia, false);
          firstVisibleMedia.classList.add('is-active');

          this.resetVariantGallerySliders();

          const mediaList = firstVisibleMedia.parentElement;

          if (mediaList) {
            mediaList.scrollTo({ left: firstVisibleMedia.offsetLeft, behavior: 'auto' });
          }

          if (this.elements.thumbnails) {
            const firstVisibleThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${firstVisibleMedia.dataset.mediaId}"]`
            );

            this.setActiveThumbnail(firstVisibleThumbnail);
          }
        }
      }

      filterVariantGalleryThumbnails(visibleMediaIds) {
        if (!this.elements.thumbnails) return;

        const thumbnailItems = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));

        thumbnailItems.forEach((thumbnail) => {
          const shouldHide = !visibleMediaIds.has(thumbnail.dataset.target);
          this.setVariantGalleryHidden(thumbnail, shouldHide);

          if (shouldHide) {
            thumbnail.querySelector('button')?.removeAttribute('aria-current');
          }
        });

        this.resetVariantGallerySliders();
      }

      filterVariantGalleryModal(selectedColor) {
        const sectionId = this.id.replace('MediaGallery-', '');
        const modal = document.querySelector(`#ProductModal-${sectionId}`);

        if (!modal) return;

        const modalMediaItems = Array.from(modal.querySelectorAll('[data-media-id]'));

        if (!modalMediaItems.length) return;

        const matchingModalItems = modalMediaItems.filter((item) => this.mediaElementMatchesColor(item, selectedColor));

        if (!matchingModalItems.length) return;

        const visibleModalIds = new Set(matchingModalItems.map((item) => item.dataset.mediaId));

        modalMediaItems.forEach((item) => {
          const shouldHide = !visibleModalIds.has(item.dataset.mediaId);
          this.setVariantGalleryHidden(item, shouldHide);

          if (shouldHide) {
            item.classList.remove('active');
          }
        });
      }

      setVariantGalleryHidden(element, shouldHide) {
        if (!element) return;

        element.hidden = shouldHide;
        element.classList.toggle('variant-gallery-hidden', shouldHide);
        element.toggleAttribute('aria-hidden', shouldHide);

        if (shouldHide) {
          element.style.setProperty('display', 'none', 'important');
          element.style.setProperty('visibility', 'hidden', 'important');
          element.style.setProperty('opacity', '0', 'important');
          element.style.setProperty('width', '0', 'important');
          element.style.setProperty('min-width', '0', 'important');
          element.style.setProperty('max-width', '0', 'important');
          element.style.setProperty('flex', '0 0 0', 'important');
          element.style.setProperty('padding', '0', 'important');
          element.style.setProperty('margin', '0', 'important');
          element.style.setProperty('overflow', 'hidden', 'important');
          element.style.setProperty('pointer-events', 'none', 'important');
        } else {
          element.style.removeProperty('display');
          element.style.removeProperty('visibility');
          element.style.removeProperty('opacity');
          element.style.removeProperty('width');
          element.style.removeProperty('min-width');
          element.style.removeProperty('max-width');
          element.style.removeProperty('flex');
          element.style.removeProperty('padding');
          element.style.removeProperty('margin');
          element.style.removeProperty('overflow');
          element.style.removeProperty('pointer-events');
        }
      }

      resetVariantGallerySliders() {
        if (this.elements.viewer && this.elements.viewer.resetPages) {
          this.elements.viewer.resetPages();
        }

        if (this.elements.thumbnails && this.elements.thumbnails.resetPages) {
          this.elements.thumbnails.resetPages();
        }
      }

      updateVariantGalleryCounter(visibleCount) {
        if (!this.elements.viewer) return;

        const currentCounter = this.elements.viewer.querySelector('.slider-counter--current');
        const totalCounter = this.elements.viewer.querySelector('.slider-counter--total');

        if (currentCounter) {
          currentCounter.textContent = '1';
        }

        if (totalCounter && visibleCount > 0) {
          totalCounter.textContent = visibleCount;
        }
      }

      showAllVariantGalleryMedia() {
        this.elements.viewer?.querySelectorAll('[data-media-id]').forEach((item) => {
          this.setVariantGalleryHidden(item, false);
        });

        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((item) => {
          this.setVariantGalleryHidden(item, false);
        });

        const sectionId = this.id.replace('MediaGallery-', '');
        const modal = document.querySelector(`#ProductModal-${sectionId}`);

        modal?.querySelectorAll('[data-media-id]').forEach((item) => {
          this.setVariantGalleryHidden(item, false);
        });

        this.resetVariantGallerySliders();
      }

      getSelectedVariantColor() {
        const productInfo = this.closest('product-info');
        const variantSelects = productInfo?.querySelector('variant-selects');

        if (!variantSelects) return null;

        const colorOptionNames = ['color', 'colour', 'couleur', 'farbe', 'colore', 'cor'];

        const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset'));

        for (const fieldset of fieldsets) {
          const legend = fieldset.querySelector('legend');

          if (!legend) continue;

          const legendClone = legend.cloneNode(true);
          legendClone.querySelectorAll('[data-selected-value]').forEach((element) => element.remove());

          const optionName = this.normalizeVariantGalleryText(legendClone.textContent);

          if (colorOptionNames.some((colorName) => optionName.includes(colorName))) {
            const checkedInput = fieldset.querySelector('input:checked');
            const selectedValue = fieldset.querySelector('[data-selected-value]');

            return this.normalizeVariantGalleryText(
              checkedInput?.value || selectedValue?.textContent || ''
            );
          }
        }

        const dropdowns = Array.from(variantSelects.querySelectorAll('.product-form__input--dropdown'));

        for (const dropdown of dropdowns) {
          const label = dropdown.querySelector('label');
          const select = dropdown.querySelector('select');

          if (!label || !select) continue;

          const optionName = this.normalizeVariantGalleryText(label.textContent);

          if (colorOptionNames.some((colorName) => optionName.includes(colorName))) {
            return this.normalizeVariantGalleryText(select.value);
          }
        }

        const swatchInput = variantSelects.querySelector('.product-form__input--swatch input:checked');

        if (swatchInput) {
          return this.normalizeVariantGalleryText(swatchInput.value);
        }

        return null;
      }

      mediaElementMatchesColor(element, selectedColor) {
        if (!element || !selectedColor) return false;

        const image = element.querySelector('img');

        if (!image) return false;

        const filename = this.getImageFilename(image);

        if (!filename) return false;

        const normalizedFilename = this.normalizeVariantGalleryText(filename);
        const normalizedColor = this.normalizeVariantGalleryText(selectedColor);

        if (!normalizedFilename || !normalizedColor) return false;

        const escapedColor = normalizedColor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const colorPattern = new RegExp(`(^|-)${escapedColor}(-|$)`);

        return colorPattern.test(normalizedFilename);
      }

      getImageFilename(image) {
        const imageSource =
          image.currentSrc ||
          image.src ||
          image.getAttribute('src') ||
          image.getAttribute('data-src') ||
          '';

        if (!imageSource) return '';

        try {
          const url = new URL(imageSource, window.location.origin);
          const pathnameParts = url.pathname.split('/');
          return decodeURIComponent(pathnameParts[pathnameParts.length - 1] || '');
        } catch (error) {
          const cleanSource = imageSource.split('?')[0];
          const sourceParts = cleanSource.split('/');
          return decodeURIComponent(sourceParts[sourceParts.length - 1] || '');
        }
      }

      normalizeVariantGalleryText(value) {
        return String(value || '')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/&/g, 'and')
          .replace(/['"]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }
    }
  );
}